import { useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { parseCsv, toCsv, downloadCsv } from "@/lib/csv";

/**
 * CsvImporter — shared shell used by /admin/import/{users,bookings,picklists}.
 *
 * Props:
 *   title          — heading shown above the importer
 *   testid         — root data-testid prefix; sub-elements get `${prefix}-{slot}`
 *   columns        — Array<{ key, label, required?, description?, example? }>
 *                    Defines the canonical schema. Order = template column order.
 *   loadContext    — async () => ctx  (e.g. {sitesByCode, clientsBySlug}). Run
 *                    once when the user lands on the page; passed into
 *                    validateRow/commitRow.
 *   validateRow    — (rawRow:object, ctx) => { ok, level: "valid"|"warn"|"error",
 *                                              normalised, errors: string[] }
 *                    `normalised` is what gets passed to commitRow.
 *   commitRow      — async (normalised, ctx) => { ok, error?:string }
 *   validateAll    — optional (rawRows[], ctx) => results[]; replaces validateRow
 *                    when cross-row checks (in-file duplicates) are needed.
 *   reconcile      — optional async (normalisedRows[], ctx) => Set<number> of
 *                    indexes NOT found after commit (post-import missing check).
 *   previewLabel   — optional (result) => string shown in a "Will create" column.
 *
 * State machine: idle → mapping → preview → committing → done.
 */
export default function CsvImporter({
  title,
  testid,
  columns,
  loadContext,
  validateRow,
  commitRow,
  validateAll,
  reconcile,
  previewLabel,
}) {
  const [stage, setStage]       = useState("idle");         // idle|mapping|preview|committing|done
  const [csv, setCsv]           = useState(null);            // { headers, rows }
  const [mapping, setMapping]   = useState({});              // canonicalKey -> headerIndex|null
  const [validated, setValidated] = useState([]);            // [{level, normalised, errors, raw}]
  const [committed, setCommitted] = useState({ ok: 0, fail: 0, errors: [], report: [] });
  const [ctx, setCtx]           = useState(null);
  const fileRef = useRef(null);

  const requiredKeys = useMemo(() => columns.filter((c) => c.required).map((c) => c.key), [columns]);

  /* ---------- 1. Upload ---------- */
  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.headers.length === 0) {
      toast.error("That file looks empty.");
      return;
    }

    // Auto-map: case-insensitive exact match against header text.
    const auto = {};
    columns.forEach((c) => {
      const idx = parsed.headers.findIndex(
        (h) => h.toLowerCase().trim() === c.key.toLowerCase()
          || h.toLowerCase().trim() === c.label.toLowerCase()
      );
      auto[c.key] = idx >= 0 ? idx : null;
    });

    // Lazy-load FK context the first time we have a file.
    let nextCtx = ctx;
    if (!nextCtx && loadContext) {
      try { nextCtx = await loadContext(); setCtx(nextCtx); }
      catch (e) { toast.error(`Could not load lookup data: ${e.message || e}`); return; }
    }

    setCsv(parsed);
    setMapping(auto);
    setStage("mapping");
  };

  /* ---------- 2. Validate (dry-run) ---------- */
  const runDryRun = () => {
    // Sanity: every required column must be mapped.
    const missing = requiredKeys.filter((k) => mapping[k] === null || mapping[k] === undefined);
    if (missing.length) {
      toast.error(`Map these required columns first: ${missing.join(", ")}`);
      return;
    }
    const raws = csv.rows.map((cells) => {
      const raw = {};
      columns.forEach((c) => {
        const idx = mapping[c.key];
        raw[c.key] = (idx !== null && idx !== undefined && idx >= 0)
          ? (cells[idx] ?? "").trim()
          : "";
      });
      return raw;
    });
    const results = validateAll ? validateAll(raws, ctx) : raws.map((raw) => validateRow(raw, ctx));
    setValidated(raws.map((raw, i) => ({ raw, rowNo: i + 1, ...results[i] })));
    setStage("preview");
  };

  /* ---------- 3. Commit ---------- */
  const runCommit = async () => {
    setStage("committing");
    const valid = validated.filter((r) => r.level === "valid" || r.level === "warn");
    let ok = 0, fail = 0;
    const errors = [];
    const outcome = new Map();
    validated.forEach((r) => {
      if (r.level === "error") outcome.set(r.rowNo, { status: "skipped", reason: r.errors.join("; ") });
    });
    const okRows = [];

    for (let i = 0; i < valid.length; i++) {
      const row = valid[i];
      try {
        const res = await commitRow(row.normalised, ctx);
        if (res?.ok) { ok++; okRows.push(row); outcome.set(row.rowNo, { status: "imported", reason: row.errors.join("; ") }); }
        else {
          fail++;
          errors.push({ row_index: row.rowNo, raw: row.raw, error: res?.error || "unknown" });
          outcome.set(row.rowNo, { status: "failed", reason: res?.error || "unknown" });
        }
      } catch (e) {
        fail++;
        errors.push({ row_index: row.rowNo, raw: row.raw, error: e.message || String(e) });
        outcome.set(row.rowNo, { status: "failed", reason: e.message || String(e) });
      }
    }

    let missing = 0;
    if (reconcile && okRows.length) {
      try {
        const notFound = await reconcile(okRows.map((r) => r.normalised), ctx);
        okRows.forEach((r, i) => {
          if (notFound.has(i)) {
            missing++;
            outcome.set(r.rowNo, { status: "missing", reason: "Reported as imported but not found in database" });
          }
        });
      } catch (e) {
        toast.error(`Post-import check failed: ${e.message || e}`);
      }
    }
    const report = validated.map((r) => ({
      row_index: r.rowNo,
      status: outcome.get(r.rowNo)?.status || "skipped",
      will_create: previewLabel ? previewLabel(r) : "",
      notes: outcome.get(r.rowNo)?.reason || "",
      ...r.raw,
    }));
    setCommitted({ ok: ok - missing, fail, errors, report, missing, skipped: validated.length - valid.length, total: validated.length });
    setStage("done");
    toast[fail === 0 ? "success" : "warning"](`Imported ${ok} of ${valid.length} rows`);
  };

  /* ---------- 4. Download helpers ---------- */
  const downloadTemplate = () => {
    const headerRow = columns.map((c) => c.key);
    const exampleRow = columns.map((c) => c.example ?? "");
    const csvStr = `${headerRow.join(",")}\n${exampleRow.map((v) => {
      const s = String(v);
      return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")}`;
    downloadCsv(`${testid}-template.csv`, csvStr);
  };

  const downloadErrors = () => {
    const rows = committed.errors.map((e) => ({
      row_index: e.row_index,
      error: e.error,
      ...e.raw,
    }));
    const cols = ["row_index", "error", ...columns.map((c) => c.key)];
    downloadCsv(`${testid}-errors.csv`, toCsv(rows, cols));
  };

  const downloadReport = () => {
    const cols = ["row_index", "status", ...(previewLabel ? ["will_create"] : []), "notes", ...columns.map((c) => c.key)];
    downloadCsv(`${testid}-report.csv`, toCsv(committed.report, cols));
  };

  const reset = () => {
    setCsv(null); setMapping({}); setValidated([]);
    setCommitted({ ok: 0, fail: 0, errors: [], report: [] });
    setStage("idle");
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ===================== RENDER ===================== */
  const counts = useMemo(() => ({
    valid: validated.filter((r) => r.level === "valid").length,
    warn:  validated.filter((r) => r.level === "warn").length,
    error: validated.filter((r) => r.level === "error").length,
  }), [validated]);

  return (
    <div className="space-y-4" data-testid={`${testid}-root`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" data-testid={`${testid}-title`}>{title}</h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload a CSV. We&apos;ll preview every row with validation results before any data is written.
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} data-testid={`${testid}-template-btn`}>
          <Download className="w-4 h-4 mr-2" /> Template CSV
        </Button>
      </div>

      {/* Schema reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Expected columns</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {columns.map((c) => (
              <div key={c.key} className="flex items-start gap-2">
                <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{c.key}</code>
                {c.required && <Badge variant="destructive" className="text-[10px]">required</Badge>}
                <span className="text-slate-600">{c.description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stage 1: upload */}
      {stage === "idle" && (
        <Card>
          <CardContent className="py-12 flex flex-col items-center justify-center gap-3 text-center">
            <FileSpreadsheet className="w-10 h-10 text-slate-400" />
            <p className="text-sm text-slate-600">Drop a .csv here or click below.</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`${testid}-file-input`}
            />
            <Button onClick={() => fileRef.current?.click()} data-testid={`${testid}-upload-btn`}>
              <Upload className="w-4 h-4 mr-2" /> Choose CSV
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stage 2: header mapping */}
      {stage === "mapping" && csv && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Map columns</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">
              {csv.rows.length} data rows detected. Confirm each field maps to the right CSV column.
            </p>
            {columns.map((c) => (
              <div key={c.key} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4 text-sm flex items-center gap-2">
                  <code className="font-mono text-xs">{c.key}</code>
                  {c.required && <span className="text-rose-600 text-xs">*</span>}
                </div>
                <div className="col-span-8">
                  <Select
                    value={mapping[c.key] === null || mapping[c.key] === undefined ? "__none__" : String(mapping[c.key])}
                    onValueChange={(v) => setMapping((m) => ({ ...m, [c.key]: v === "__none__" ? null : Number(v) }))}
                  >
                    <SelectTrigger data-testid={`${testid}-map-${c.key}`}>
                      <SelectValue placeholder="(skip)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">(skip)</SelectItem>
                      {csv.headers.map((h, i) => (
                        <SelectItem key={i} value={String(i)}>{h || `(col ${i + 1})`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={reset}>Back</Button>
              <Button onClick={runDryRun} data-testid={`${testid}-dryrun-btn`}>Preview</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stage 3: preview */}
      {stage === "preview" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-3">
              Preview
              <span className="flex gap-2 text-xs font-normal">
                <Badge variant="outline" data-testid={`${testid}-count-importable`}>{counts.valid + counts.warn} will import</Badge>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`${testid}-count-valid`}>{counts.valid} valid</Badge>
                {counts.warn > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`${testid}-count-warn`}>{counts.warn} warnings</Badge>}
                {counts.error > 0 && <Badge variant="destructive" data-testid={`${testid}-count-error`}>{counts.error} errors</Badge>}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="max-h-[400px] overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left w-10">#</th>
                    <th className="px-2 py-1.5 text-left w-24">Status</th>
                    {previewLabel && <th className="px-2 py-1.5 text-left">Will create</th>}
                    {columns.map((c) => (
                      <th key={c.key} className="px-2 py-1.5 text-left">{c.key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {validated.slice(0, 200).map((r, i) => (
                    <tr key={i} className={r.level === "error" ? "bg-rose-50" : r.level === "warn" ? "bg-amber-50" : ""}>
                      <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                      <td className="px-2 py-1">
                        {r.level === "valid" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline" />}
                        {r.level === "warn"  && <AlertCircle className="w-3.5 h-3.5 text-amber-600 inline" />}
                        {r.level === "error" && <AlertCircle className="w-3.5 h-3.5 text-rose-600 inline" />}
                        {r.errors?.length > 0 && (
                          <span className={`ml-1 ${r.level === "warn" ? "text-amber-700" : "text-rose-700"}`}>{r.errors.join("; ")}</span>
                        )}
                      </td>
                      {previewLabel && <td className="px-2 py-1 text-[11px] font-medium text-slate-900" data-testid={`${testid}-will-create-${i + 1}`}>{previewLabel(r)}</td>}
                      {columns.map((c) => (
                        <td key={c.key} className="px-2 py-1 font-mono text-[11px] text-slate-700">{r.raw[c.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {validated.length > 200 && (
                <p className="text-[11px] text-slate-500 px-2 py-1">…showing first 200 of {validated.length} rows.</p>
              )}
            </div>
            {counts.error > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  {counts.error} rows have validation errors and will be <strong>skipped</strong> on commit.
                </AlertDescription>
              </Alert>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStage("mapping")}>Back</Button>
              <Button variant="ghost" onClick={reset}>Start over</Button>
              <Button
                onClick={runCommit}
                disabled={counts.valid + counts.warn === 0}
                data-testid={`${testid}-commit-btn`}
              >
                Import {counts.valid + counts.warn} {(counts.valid + counts.warn) === 1 ? "row" : "rows"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stage 4: committing */}
      {stage === "committing" && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-600">
            Importing… please don&apos;t close this tab.
          </CardContent>
        </Card>
      )}

      {/* Stage 5: done */}
      {stage === "done" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Done</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm" data-testid={`${testid}-result`}>
              <span className="text-emerald-700 font-semibold">{committed.ok} imported</span>
              {committed.fail > 0 && <>, <span className="text-rose-700 font-semibold">{committed.fail} failed</span></>}
              {committed.skipped > 0 && <>, <span className="text-slate-600 font-semibold">{committed.skipped} skipped</span></>}
              {reconcile && <>, <span className={committed.missing ? "text-rose-700 font-semibold" : "text-slate-600"} data-testid={`${testid}-missing`}>{committed.missing || 0} missing after verification</span></>}
              {" "}of {committed.total} rows.
            </p>
            {committed.report.length > 0 && (
              <Button variant="outline" size="sm" onClick={downloadReport} data-testid={`${testid}-download-report`}>
                <Download className="w-4 h-4 mr-2" /> Download full report CSV
              </Button>
            )}
            {committed.report.some((r) => r.status !== "imported") && (
              <div className="max-h-[240px] overflow-auto border rounded text-xs" data-testid={`${testid}-not-imported`}>
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0"><tr><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Status</th><th className="px-2 py-1 text-left">Reason</th></tr></thead>
                  <tbody>
                    {committed.report.filter((r) => r.status !== "imported").map((r) => (
                      <tr key={r.row_index}>
                        <td className="px-2 py-1 text-slate-400">{r.row_index}</td>
                        <td className="px-2 py-1 font-medium">{r.status}</td>
                        <td className="px-2 py-1 text-rose-700">{r.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {committed.errors.length > 0 && (
              <>
                <div className="max-h-[200px] overflow-auto border rounded text-xs">
                  <table className="w-full">
                    <thead className="bg-slate-50"><tr><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Error</th></tr></thead>
                    <tbody>
                      {committed.errors.slice(0, 50).map((e, i) => (
                        <tr key={i}><td className="px-2 py-1 text-slate-400">{e.row_index}</td><td className="px-2 py-1 text-rose-700">{e.error}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button variant="outline" size="sm" onClick={downloadErrors} data-testid={`${testid}-download-errors`}>
                  <Download className="w-4 h-4 mr-2" /> Download errors CSV
                </Button>
              </>
            )}
            <div className="pt-2">
              <Button onClick={reset} data-testid={`${testid}-reset-btn`}>Import another file</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
